import type { ValueResolver } from './expression.model';

/**
 * Token types for the expression lexer.
 */
type TokenType = 'number' | 'path' | 'op' | 'lparen' | 'rparen' | 'comma';

interface Token {
  type: TokenType;
  value: string;
}

/**
 * Tokenize an expression string into a list of tokens.
 *
 * Hyphen ambiguity rule: greedy path matching.
 * `f-tangent.slope` (no spaces around `-`) → single path token.
 * `f.h - g.h` (spaces around `-`) → three tokens: path, op, path.
 *
 * Path pattern: [a-zA-Z_][a-zA-Z0-9_-]*(.[a-zA-Z0-9_][a-zA-Z0-9_]*)*(\[\d+\])?
 */
function tokenize(expr: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < expr.length) {
    const ch = expr[i];

    // Skip whitespace
    if (ch === ' ' || ch === '\t') {
      i++;
      continue;
    }

    // Number literal (including decimals)
    if (ch >= '0' && ch <= '9') {
      let num = '';
      while (i < expr.length && ((expr[i] >= '0' && expr[i] <= '9') || expr[i] === '.')) {
        num += expr[i];
        i++;
      }
      tokens.push({ type: 'number', value: num });
      continue;
    }

    // Identifier/path: starts with [a-zA-Z_]
    if ((ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_') {
      let path = '';
      // First segment (allows hyphens for graphpad element IDs like "f-tangent")
      while (
        i < expr.length &&
        ((expr[i] >= 'a' && expr[i] <= 'z') ||
          (expr[i] >= 'A' && expr[i] <= 'Z') ||
          (expr[i] >= '0' && expr[i] <= '9') ||
          expr[i] === '_' ||
          expr[i] === '-')
      ) {
        path += expr[i];
        i++;
      }
      // Dot-separated segments
      while (i < expr.length && expr[i] === '.') {
        if (
          i + 1 < expr.length &&
          ((expr[i + 1] >= 'a' && expr[i + 1] <= 'z') ||
            (expr[i + 1] >= 'A' && expr[i + 1] <= 'Z') ||
            (expr[i + 1] >= '0' && expr[i + 1] <= '9') ||
            expr[i + 1] === '_')
        ) {
          path += '.';
          i++; // skip the dot
          while (
            i < expr.length &&
            ((expr[i] >= 'a' && expr[i] <= 'z') ||
              (expr[i] >= 'A' && expr[i] <= 'Z') ||
              (expr[i] >= '0' && expr[i] <= '9') ||
              expr[i] === '_')
          ) {
            path += expr[i];
            i++;
          }
        } else {
          break;
        }
      }
      // Array index: [0], [1], etc.
      if (i < expr.length && expr[i] === '[') {
        path += '[';
        i++;
        while (i < expr.length && expr[i] !== ']') {
          path += expr[i];
          i++;
        }
        if (i < expr.length && expr[i] === ']') {
          path += ']';
          i++;
        }
      }
      tokens.push({ type: 'path', value: path });
      continue;
    }

    // Comma (for function calls)
    if (ch === ',') {
      tokens.push({ type: 'comma', value: ',' });
      i++;
      continue;
    }

    // Multi-char operators
    if (i + 1 < expr.length) {
      const two = ch + expr[i + 1];
      if (two === '>=' || two === '<=' || two === '==' || two === '!=' || two === '&&' || two === '||') {
        tokens.push({ type: 'op', value: two });
        i += 2;
        continue;
      }
    }

    // Single-char operators
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/' || ch === '>' || ch === '<') {
      tokens.push({ type: 'op', value: ch });
      i++;
      continue;
    }

    // Negation operator
    if (ch === '!') {
      tokens.push({ type: 'op', value: '!' });
      i++;
      continue;
    }

    // Parentheses
    if (ch === '(') {
      tokens.push({ type: 'lparen', value: '(' });
      i++;
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen', value: ')' });
      i++;
      continue;
    }

    throw new Error(`Unexpected character '${ch}' in expression: ${expr}`);
  }

  return tokens;
}

// Built-in math functions
const MATH_FUNCTIONS: Record<string, (...args: number[]) => number> = {
  sqrt: (x) => Math.sqrt(x),
  abs: (x) => Math.abs(x),
  sin: (x) => Math.sin(x),
  cos: (x) => Math.cos(x),
  tan: (x) => Math.tan(x),
  asin: (x) => Math.asin(x),
  acos: (x) => Math.acos(x),
  atan: (x) => Math.atan(x),
  floor: (x) => Math.floor(x),
  ceil: (x) => Math.ceil(x),
  round: (x) => Math.round(x),
  deg: (x) => (x * 180) / Math.PI,
  rad: (x) => (x * Math.PI) / 180,
  log: (x) => Math.log(x),
  exp: (x) => Math.exp(x),
  pow: (base, exp) => Math.pow(base, exp),
  atan2: (y, x) => Math.atan2(y, x),
  min: (...args) => Math.min(...args),
  max: (...args) => Math.max(...args),
};

/**
 * Parse an expression string into a reactive thunk that evaluates against a ValueResolver.
 *
 * Supports: numeric literals, path references, arithmetic (+, -, *, /),
 * comparison (>, <, >=, <=, ==, !=), logical (&&, ||, !), math functions
 * (sqrt, sin, cos, tan, abs, floor, ceil, round, deg, rad, atan2, min, max),
 * parentheses, and standard operator precedence.
 *
 * Comparisons and logical ops return 1 (true) / 0 (false).
 *
 * @returns A thunk `() => number` that evaluates the expression reactively.
 */
export function parseExpressionThunk(expr: string, resolver: ValueResolver): () => number {
  const tokens = tokenize(expr);
  let pos = 0;

  function peek(): Token | undefined {
    return tokens[pos];
  }

  function consume(): Token {
    return tokens[pos++];
  }

  function parsePrimary(): () => number {
    const token = peek();

    if (!token) {
      throw new Error(`Unexpected end of expression: ${expr}`);
    }

    // Unary minus
    if (token.type === 'op' && token.value === '-') {
      consume();
      const operand = parsePrimary();
      return () => -operand();
    }

    // Unary plus
    if (token.type === 'op' && token.value === '+') {
      consume();
      return parsePrimary();
    }

    // Logical negation
    if (token.type === 'op' && token.value === '!') {
      consume();
      const operand = parsePrimary();
      return () => (operand() !== 0 ? 0 : 1);
    }

    if (token.type === 'number') {
      consume();
      const val = parseFloat(token.value);
      return () => val;
    }

    if (token.type === 'path') {
      consume();
      const name = token.value;

      // Check if it's a function call: path followed by '('
      if (peek()?.type === 'lparen' && name in MATH_FUNCTIONS) {
        consume(); // skip '('
        const args: Array<() => number> = [parseLogicalOr()];

        while (peek()?.type === 'comma') {
          consume(); // skip ','
          args.push(parseLogicalOr());
        }

        if (!peek() || peek()!.type !== 'rparen') {
          throw new Error(`Expected ')' after ${name}() arguments in expression: ${expr}`);
        }
        consume(); // skip ')'

        const fn = MATH_FUNCTIONS[name];
        return () => fn(...args.map((a) => a()));
      }

      // Path reference
      return () => resolver.resolveNumber(name);
    }

    if (token.type === 'lparen') {
      consume();
      const inner = parseLogicalOr();
      if (!peek() || peek()!.type !== 'rparen') {
        throw new Error(`Expected ')' in expression: ${expr}`);
      }
      consume();
      return inner;
    }

    throw new Error(`Unexpected token '${token.value}' in expression: ${expr}`);
  }

  function parseMulDiv(): () => number {
    let left = parsePrimary();

    while (peek()?.type === 'op' && (peek()!.value === '*' || peek()!.value === '/')) {
      const op = consume().value;
      const right = parsePrimary();
      const l = left;
      if (op === '*') {
        left = () => l() * right();
      } else {
        left = () => l() / right();
      }
    }

    return left;
  }

  function parseAddSub(): () => number {
    let left = parseMulDiv();

    while (peek()?.type === 'op' && (peek()!.value === '+' || peek()!.value === '-')) {
      const op = consume().value;
      const right = parseMulDiv();
      const l = left;
      if (op === '+') {
        left = () => l() + right();
      } else {
        left = () => l() - right();
      }
    }

    return left;
  }

  function parseComparison(): () => number {
    let left = parseAddSub();
    const t = peek();
    if (
      t?.type === 'op' &&
      (t.value === '>' ||
        t.value === '<' ||
        t.value === '>=' ||
        t.value === '<=' ||
        t.value === '==' ||
        t.value === '!=')
    ) {
      const op = consume().value;
      const right = parseAddSub();
      const l = left;
      switch (op) {
        case '>':
          left = () => (l() > right() ? 1 : 0);
          break;
        case '<':
          left = () => (l() < right() ? 1 : 0);
          break;
        case '>=':
          left = () => (l() >= right() ? 1 : 0);
          break;
        case '<=':
          left = () => (l() <= right() ? 1 : 0);
          break;
        case '==':
          left = () => (l() === right() ? 1 : 0);
          break;
        case '!=':
          left = () => (l() !== right() ? 1 : 0);
          break;
      }
    }
    return left;
  }

  function parseLogicalAnd(): () => number {
    let left = parseComparison();
    while (peek()?.type === 'op' && peek()!.value === '&&') {
      consume();
      const right = parseComparison();
      const l = left;
      left = () => (l() !== 0 && right() !== 0 ? 1 : 0);
    }
    return left;
  }

  function parseLogicalOr(): () => number {
    let left = parseLogicalAnd();
    while (peek()?.type === 'op' && peek()!.value === '||') {
      consume();
      const right = parseLogicalAnd();
      const l = left;
      left = () => (l() !== 0 || right() !== 0 ? 1 : 0);
    }
    return left;
  }

  const result = parseLogicalOr();

  if (pos < tokens.length) {
    throw new Error(`Unexpected token '${tokens[pos].value}' after expression: ${expr}`);
  }

  return result;
}

/**
 * Evaluate an expression string immediately against a ValueResolver.
 * Convenience wrapper that creates and immediately calls the thunk.
 */
export function evaluateExpression(expr: string, resolver: ValueResolver): number {
  return parseExpressionThunk(expr, resolver)();
}

/**
 * Extract all path references from an expression string.
 * Used for dependency resolution — returns the paths referenced.
 * Filters out known math function names.
 */
export function extractExpressionPaths(expr: string): string[] {
  try {
    const tokens = tokenize(expr);
    return tokens.filter((t) => t.type === 'path' && !(t.value in MATH_FUNCTIONS)).map((t) => t.value);
  } catch {
    return [];
  }
}
